import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { MdThumbDown, MdThumbUp } from "react-icons/md";

export type TFeedbackData = {
  isLiked: boolean;
  rating: 'like' | 'dislike';
  comment: string;
};

export type TFeedbackAlertProps = {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  backdrop?: boolean | "static";
  keyboard?: boolean;
  title?: string | JSX.Element | JSX.Element[];
  description?: string | JSX.Element | JSX.Element[];
  onSubmit?: (feedback: TFeedbackData) => void;
  onHome?: () => void;
  onClose?: () => void;
  buttonLabel?: string;
  defaultRating?: "like" | "dislike" | null;
};

FeedbackAlert.defaultProps = {
  show: false,
  setShow: () => {},
  backdrop: "static",
  keyboard: false,
  title: "Feedback",
  description: "How would you rate your experience?",
  onSubmit: () => {},
  onHome: () => {},
  onClose: () => {},
  buttonLabel: "Submit",
  defaultRating: null,
};

export default function FeedbackAlert(props: TFeedbackAlertProps) {
  const [rating, setRating] = useState<'like' | 'dislike' | null>(props.defaultRating);
  const [comment, setComment] = useState<string>('');

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleRatingSelect = (selectedRating: 'like' | 'dislike') => {
    setRating(selectedRating);
  };

  useEffect(() => {
    setRating(props.defaultRating);
  }, [props.defaultRating]);

  useEffect(() => {
    if (rating && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [rating]);

  const handleButtonClick = () => {
    if (rating) {
      const feedbackData: TFeedbackData = {
        isLiked: rating === 'like',
        rating: rating,
        comment: comment
      };
      props.onSubmit?.(feedbackData);
    } else {
      props.onHome?.();
    }
    props.setShow(false);

    setTimeout(() => {
      setRating(null);
      setComment('');
    }, 300);
  };

  const handleClose = () => {
    props.setShow(false);
    props.onClose?.();

    setTimeout(() => {
      setRating(null);
      setComment('');
    }, 300);
  };

  return (
    <Modal
      className="modal-generic feedback-alert"
      show={props.show}
      backdrop={props.backdrop}
      keyboard={props.keyboard}
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>{props.title ?? "Feedback"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="feedback-description">
          {props.description ?? "How would you rate your experience?"}
        </div>

        <div className="rating-buttons">
          <button
            className={`rating-button ${rating === 'like' ? 'selected' : ''}`}
            onClick={() => handleRatingSelect('like')}
          >
            <div className={`icon-container thumb-up ${rating === 'dislike' ? 'disabled' : ''}`}>
              <span><MdThumbUp /></span>
            </div>
          </button>

          <button
            className={`rating-button ${rating === 'dislike' ? 'selected' : ''}`}
            onClick={() => handleRatingSelect('dislike')}
          >
            <div className={`icon-container thumb-down ${rating === 'like' ? 'disabled' : ''}`}>
              <span><MdThumbDown /></span>
            </div>
          </button>
        </div>

        {rating && (
          <div className="feedback-input">
            <label>We'd love to hear your feedback</label>
            <textarea
              ref={textareaRef}
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your thoughts..."
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-mw btn-primary" onClick={handleButtonClick}>
          {rating ? props.buttonLabel : "Home"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
